import { FormikErrors, setIn } from 'formik';

import { PaymentTermReference } from '../../../types/PaymentTermReference';
import { SamplekitMessage } from '../../../types/SamplekitMessage';
import { SurchargeMessage } from '../../../types/SurchargeMessage';
import { VesselMessage } from '../../../types/VesselMessage';
import { translate } from '../../common/helpers/translate.helper';
import { ItemModel } from '../../common/models/ItemModel';

interface RfqQuoteFormValues {
    vessel: VesselMessage;
    portId: string;
    dateDelivery: string;
    noticeDays: number | null;
    paymentTermReference?: PaymentTermReference;
    paymentTermReferenceDays: number | null;
    supplyMode: string;
    vendorReference: string;
    items: ItemModel[];
    surcharges: SurchargeMessage[];
    samplekits?: SamplekitMessage[];
}

interface RfqQuoteFormikErrors extends FormikErrors<RfqQuoteFormValues> {
    invalidProducts?: boolean;
}

export function useValidateRfqQuoteForm(isSpot: boolean): (formikValues: RfqQuoteFormValues) => RfqQuoteFormikErrors {
    return (formikValues: RfqQuoteFormValues) => {
        let errors: RfqQuoteFormikErrors = {};

        if (isSpot && !formikValues.paymentTermReference) {
            errors.paymentTermReference = translate('offer.edit.lubes.paymentTermReferenceRequired');
        }

        if (
            isSpot &&
            formikValues.paymentTermReference &&
            formikValues.paymentTermReference !== PaymentTermReference.PREPAYMENT &&
            !formikValues.paymentTermReferenceDays
        ) {
            errors.paymentTermReferenceDays = translate('offer.edit.lubes.paymentTermReferenceDaysRequired');
        }

        if (!formikValues.supplyMode) {
            errors.supplyMode = translate('offer.edit.lubes.supplyModeRequired');
        }

        formikValues.items.forEach((item, index) => {
            if (item.deleted) {
                return;
            }

            if (!item.productId) {
                errors = setIn(errors, `items.${index}.productId`, true);
            }

            if (!item.units || Number(item.units) === 0) {
                errors = setIn(errors, `items.${index}.units`, true);
            }

            if (!item.unitSize || Number(item.unitSize) === 0) {
                errors = setIn(errors, `items.${index}.unitSize`, true);
            }

            if (
                item.price?.value === undefined ||
                item.price?.value?.toString() === '' ||
                Number(item.price?.value) <= 0
            ) {
                errors = setIn(errors, `items.${index}.price.value`, true);
            }
        });

        if (formikValues.items.every((item) => item.deleted)) {
            errors.invalidProducts = true;
        }

        formikValues.samplekits?.forEach((samplekit, index) => {
            if (samplekit.deleted) {
                return;
            }

            if (!samplekit.name) {
                errors = setIn(errors, `samplekits.${index}.name`, true);
            }

            if (!samplekit.quantity || Number(samplekit.quantity) === 0) {
                errors = setIn(errors, `samplekits.${index}.quantity`, true);
            }

            if (
                samplekit.value?.value === undefined ||
                samplekit.value?.value?.toString() === '' ||
                Number(samplekit.value?.value) < 0
            ) {
                errors = setIn(errors, `samplekits.${index}.value.value`, true);
            }
        });

        formikValues.surcharges?.forEach((surcharge, index) => {
            if (surcharge.deleted) {
                return;
            }

            if ((surcharge.surchargeType === 'CUSTOM' || surcharge.value?.value < 0) && !surcharge.name) {
                errors = setIn(errors, `surcharges.${index}.name`, true);
            }

            if (surcharge.value?.value === undefined || surcharge.value?.value?.toString() === '') {
                errors = setIn(errors, `surcharges.${index}.value.value`, true);
            }
        });

        return errors;
    };
}
